/**
 * Single source of truth for Advanced Calculator What-If scenarios.
 * All what-ifs must call these helpers — same engine path as main Run.
 */
import { historicalData } from './data';
import { SimulationEngine } from './SimulationEngine.js';
import {
  computeFixedSustainableBinarySearch as computeFixedMSI,
  computeVanguardGuardrailsSeries as computeVGSeries,
  computeDynamicSWRSeries as computeDynSeries,
  computeFloorCeilingSeries as computeFCSeries,
} from './AdvancedCalculatorShared.js';

/** Build historical real returns — identical logic to main Run button. */
export function buildHistoricalReturnsSnapshot(startYearVal, periodYears, inputSnapshot = {}) {
  const weights = {
    sp500: Number.isFinite(Number(inputSnapshot.sp500)) ? Number(inputSnapshot.sp500) : 0,
    aus: Number.isFinite(Number(inputSnapshot.ausShares)) ? Number(inputSnapshot.ausShares) : 0,
    intl: Number.isFinite(Number(inputSnapshot.intlShares)) ? Number(inputSnapshot.intlShares) : 0,
    bonds: Number.isFinite(Number(inputSnapshot.bonds)) ? Number(inputSnapshot.bonds) : 0,
    cash: Number.isFinite(Number(inputSnapshot.cash)) ? Number(inputSnapshot.cash) : 0,
    frank: Number.isFinite(Number(inputSnapshot.frankingCredits)) ? Number(inputSnapshot.frankingCredits) : 0,
  };
  const infraPct = Number(inputSnapshot.infrastructurePct) || 0;
  const infraRate = Number(inputSnapshot.infrastructureReturnRate) || 0;
  const returns = [];
  for (let i = 0; i < periodYears; i++) {
    const year = startYearVal + i;
    const yearData = historicalData[year];
    if (!yearData) {
      throw new Error(`Missing historical data for year ${year}`);
    }
    const portfolio = {
      sp500: weights.sp500,
      ausShares: weights.aus,
      intlShares: weights.intl,
      bonds: weights.bonds,
      cash: weights.cash,
      frankingCredits: weights.frank,
      infrastructurePct: infraPct,
      infrastructureReturnRate: infraRate,
    };
    const nominalR = Number(SimulationEngine.getPortfolioReturn(yearData, portfolio)) || 0;
    const histInfl = (historicalData[year]?.inflation) || 0;
    const netR = ((1 + nominalR) / (1 + histInfl)) - 1;
    returns.push({ year, r: netR, inflation: histInfl });
  }
  return returns;
}

/** Returns from last main run, or rebuilds from snapshot if missing. */
export function resolveBaselineReturns(lastInputs, lastReturns) {
  const start = Number(lastInputs.startYear) || 1980;
  const period = Math.max(0, Number(lastInputs.periodLength) || 0);
  if (lastReturns?.length === period && lastReturns[0]?.year === start) {
    return lastReturns;
  }
  return buildHistoricalReturnsSnapshot(start, period, lastInputs);
}

/** Apply sequence-of-returns shock to first N years (percentage points). */
export function applySequenceRiskToReturns(returns, seqRiskPct, years = 3) {
  const delta = Number(seqRiskPct) / 100;
  return returns.map((row, i) => (
    i < years ? { ...row, r: row.r + delta } : row
  ));
}

function normalizeStrategyKey(strategyKey) {
  return (strategyKey || 'fixed').toLowerCase();
}

/** Full strategy result (avgIncome, incomeByYear, finalBalance). */
export function computeStrategyFullResult(inputs, strategyKey) {
  const s = normalizeStrategyKey(strategyKey);
  if (s === 'fixed' || s === 'fixedsustainableincome') {
    return computeFixedMSI(inputs);
  }
  if (s === 'vanguardguardrails') {
    return computeVGSeries(inputs);
  }
  if (s === 'dynamicswr') {
    return computeDynSeries(inputs);
  }
  if (s === 'floorandceiling' || s === 'floorceiling') {
    return computeFCSeries(inputs);
  }
  return computeFixedMSI(inputs);
}

/** MSI only (rounded). */
export function computeStrategyMSI(inputs, strategyKey) {
  return Math.round(computeStrategyFullResult(inputs, strategyKey).avgIncome || 0);
}

/** Baseline inputs + returns bundle from last main run snapshot. */
export function buildBaselineCalcInputs(lastInputs, lastReturns) {
  const returns = resolveBaselineReturns(lastInputs, lastReturns);
  return { ...lastInputs, providedReturns: returns };
}

/** What-if calc inputs: same snapshot, one parameter changed + explicit returns. */
export function buildWhatIfCalcInputs(lastInputs, lastReturns, overrides = {}) {
  const merged = { ...lastInputs, ...overrides };
  const start = Number(merged.startYear) || 1980;
  const period = Math.max(0, Number(merged.periodLength) || 0);
  const returns = overrides.providedReturns
    ?? (period !== Number(lastInputs.periodLength)
      ? buildHistoricalReturnsSnapshot(start, period, merged)
      : resolveBaselineReturns(lastInputs, lastReturns));
  return { ...merged, providedReturns: returns };
}

/**
 * ONE loop for rolling-period MSI. Main period income MUST come from
 * `current` — never call computeForWindow(selectedStart) separately.
 */
export function computeRollingPeriodAnalysis({
  selectedStartYear,
  windowLen,
  totalSuper,
  computeForWindow,
}) {
  const allYears = Object.keys(historicalData).map(Number).sort((a, b) => a - b);
  const firstYear = allYears[0];
  const lastYear = allYears[allYears.length - 1];
  const rolling = [];

  if (windowLen > 0 && totalSuper > 0 && lastYear - firstYear + 1 >= windowLen) {
    for (let s = firstYear; s <= lastYear - windowLen + 1; s++) {
      const raw = computeForWindow(s, windowLen);
      const income = typeof raw === 'number'
        ? raw
        : Math.round(Number(raw?.avgIncome) || 0);
      rolling.push({
        start: s,
        income,
        fullResult: typeof raw === 'object' && raw != null ? raw : null,
      });
    }
  }

  const selectedStart = Number(selectedStartYear);
  const current = rolling.find((r) => r.start === selectedStart) || null;
  return { rolling, current };
}
